import { useCallback } from 'react';
import { cloneDeep, last } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  uuid,
  commonUtils,
  ILayoutComponent,
  useNotifications,
  useEditorStore,
  useFlowBuilder2AddLayoutComponentsMutation,
} from '@diana/core';
import { FLOW_BUILDER2_PAGE_NS } from '../i18n';
import { useFlowBuilder2PageStore } from '../Store/useFlowBuilder2PageStore';

export const useDuplicateComponent = (
  options?: Parameters<typeof useFlowBuilder2AddLayoutComponentsMutation>[0],
) => {
  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const notifications = useNotifications();

  const getLayoutById = useEditorStore((s) => s.getLayoutById);
  const updateLayoutById = useEditorStore((s) => s.updateLayoutById);
  const setActiveComponent = useFlowBuilder2PageStore((s) => s.setActiveComponent);

  const { mutateAsync, isPending: isDuplicatingComponent } =
    useFlowBuilder2AddLayoutComponentsMutation({
      ...options,
      onSuccess: (data, variables, context) => {
        const { layoutId, newComponentId } = variables;
        if (layoutId && newComponentId)
          setActiveComponent({ layoutId, componentId: newComponentId });
        updateLayoutById(data.id, (draft) => ({ ...draft, components: data.components }));
        options?.onSuccess?.(data, variables, context);
      },
      onError: (error, variables, context) => {
        notifications.error({
          message: t('errors.duplicateComponentFailed'),
          description: commonUtils.getErrorMessage(error),
        });
        options?.onError?.(error, variables, context);
      },
    });

  const duplicateComponent = useCallback(
    async (args: { layoutId: string; componentId: string }) => {
      const { layoutId, componentId } = args;

      const layout = getLayoutById(layoutId);
      if (!layout) throw new Error('No layout found');

      const componentsMap = layout.components.reduce<Record<string, ILayoutComponent>>(
        (result, component) => {
          return { ...result, [component.id]: component };
        },
        {},
      );

      const componentToDuplicate = componentsMap[componentId];
      if (!componentToDuplicate) throw new Error('No component found');

      const data: ILayoutComponent[] = [];

      const newComponentId = uuid();
      data.push({ ...cloneDeep(componentToDuplicate), id: newComponentId });
      delete componentsMap[componentToDuplicate.id];

      (function addChildren(parentId: string, newParentId: string) {
        for (const component of layout.components) {
          if (component.parentId === parentId && componentsMap[component.id]) {
            const newComponentId = uuid();
            data.push({ ...cloneDeep(component), id: newComponentId, parentId: newParentId });
            delete componentsMap[component.id];

            addChildren(component.id, newComponentId);
          }
        }
      })(componentToDuplicate.id, data[0].id);

      return mutateAsync({
        data,
        newComponentId,
        layoutId,
        afterComponent: last(layout.components)?.id,
      });
    },
    [getLayoutById, mutateAsync],
  );

  return { duplicateComponent, isDuplicatingComponent };
};
